import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { LuX } from 'react-icons/lu';

import { logEvent } from '~/config/firebase.config';
import { ClientDatabase, IClient, Mode } from '~/types/client';
import {
    isExtendedDisplay,
    pageIsOnExternalScreen,
    openPublicScreen,
    publicScreenURL,
    regieURL,
    rememberDualScreen,
    requestScreenAccess,
    ScreenAccess,
    screenAccess,
    usedDualScreenBefore,
} from '~/services/dualScreen/dualScreen';

import { ProUpsell } from './ProUpsell';
import { useDualScreenAccess } from './useDualScreenAccess';
import styles from './DualScreen.module.css';

type Step = 'choose' | 'cable' | 'mirror' | 'moveFirst' | 'phone' | 'blocked';

function IconScreens() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <rect x="1.5" y="5" width="11" height="8" rx="1.5" />
            <rect x="11.5" y="9" width="11" height="8" rx="1.5" />
            <path d="M5 17h4M15 21h4" />
        </svg>
    );
}

export type DualScreenButtonProps = {
    client: IClient;
    /** Jeu chargé sous l'accueil de la soirée à l'ouverture de l'écran public. */
    gameID: string;
    /** Développement local : ignore la formule (page de test sans compte). */
    forceAccess?: boolean;
};

/**
 * Double écran « Régie » depuis le menu de la console : l'écran public part sur le
 * projecteur (2e fenêtre), cette page devient la régie. Formule Pro (offert à l'essai).
 */
export function DualScreenButton({ client, gameID, forceAccess = false }: DualScreenButtonProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    // Le statut d'essai demande un appel au serveur de paiement : seulement une fois le
    // bouton cliqué, pas à chaque ouverture du menu de tous les bars.
    const realAccess = useDualScreenAccess(client, open);
    const access = forceAccess ? 'yes' : realAccess;
    const knownAccess = forceAccess || client.plan === 'pro' || !!client.trialGoal;
    const [step, setStep] = useState<Step>('choose');
    const [screens, setScreens] = useState<ScreenAccess>('unsupported');
    const extended = isExtendedDisplay();
    const again = usedDualScreenBefore(client.id);

    useEffect(() => {
        screenAccess().then(setScreens);
    }, [open]);

    const close = () => {
        setOpen(false);
        setStep('choose');
    };
    const closeRef = useRef(close);
    closeRef.current = close;
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeRef.current();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    const start = () => {
        logEvent('dual_screen_open', client.id, { clientID: client.id, pro: knownAccess, extended, again });
        setOpen(true);
    };
    // L'accès n'est connu qu'après l'ouverture (appel au serveur de paiement) : journalisé à part.
    const accessLogged = useRef(false);
    useEffect(() => {
        if (!open) {
            accessLogged.current = false;
            return;
        }
        if (access === 'loading' || accessLogged.current) return;
        accessLogged.current = true;
        logEvent('dual_screen_access', client.id, { clientID: client.id, access });
    }, [open, access]);

    const prepare = () => {
        rememberDualScreen(client.id);
        // L'écran public s'ouvre sur l'accueil de la soirée (QR en grand), partie en pause
        // dessous (vue + pause en une écriture).
        ClientDatabase.setRegieView(client.id, Mode.Anim, 'lobby').catch(() => {});
    };

    const sendToProjector = async () => {
        let current = screens;
        if (current === 'prompt') {
            current = (await requestScreenAccess()) ? 'granted' : 'denied';
            setScreens(current);
            // La fenêtre d'autorisation a consommé le clic : un 2e clic ouvrira l'écran.
            if (current === 'granted') return;
        }
        if (current === 'granted' && (await pageIsOnExternalScreen())) {
            setStep('moveFirst');
            return;
        }
        prepare();
        const res = await openPublicScreen(client.id, gameID, current);
        logEvent('dual_screen_public_opened', client.id, { clientID: client.id, ...res, access: current });
        if (!res.opened) {
            setStep('blocked');
            return;
        }
        navigate(forceAccess ? `/dev/regie/${client.id}` : regieURL(client.id));
    };

    const thisIsPublic = () => {
        prepare();
        logEvent('dual_screen_phone', client.id, { clientID: client.id });
        document.documentElement.requestFullscreen?.().catch(() => {});
        navigate(publicScreenURL(client.id, gameID));
    };

    const regieLink = `${window.location.origin}${regieURL(client.id)}`;

    let body: JSX.Element;
    if (access === 'loading') {
        body = <p className={styles.p}>…</p>;
    } else if (access === 'no') {
        body = <ProUpsell clientID={client.id} onClose={close} />;
    } else if (step === 'choose') {
        body = (
            <>
                <h2 className={styles.title}>{t('regie.setup.where')}</h2>
                <div className={styles.options}>
                    <button
                        type="button"
                        className={styles.option}
                        onClick={() => setStep(extended === false ? 'mirror' : 'cable')}
                    >
                        <b>{t('regie.setup.cable')}</b>
                        <span>{t('regie.setup.cableHint')}</span>
                    </button>
                    <button type="button" className={styles.option} onClick={() => setStep('phone')}>
                        <b>{t('regie.setup.phone')}</b>
                        <span>{t('regie.setup.phoneHint')}</span>
                    </button>
                </div>
            </>
        );
    } else if (step === 'mirror') {
        body = (
            <>
                <h2 className={styles.title}>{t('regie.setup.mirrorTitle')}</h2>
                <p className={styles.p}>{t('regie.setup.mirrorText')}</p>
                <div className={styles.two}>
                    <div>
                        <b>Windows</b>
                        <span>{t('regie.setup.mirrorWindows')}</span>
                    </div>
                    <div>
                        <b>Mac</b>
                        <span>{t('regie.setup.mirrorMac')}</span>
                    </div>
                </div>
                <button type="button" className={styles.primary} onClick={() => setStep('cable')}>
                    {t('regie.setup.mirrorDone')}
                </button>
            </>
        );
    } else if (step === 'cable') {
        const auto = screens === 'granted' || screens === 'prompt';
        body = (
            <>
                <h2 className={styles.title}>{t('regie.setup.cableTitle')}</h2>
                <ol className={styles.steps}>
                    <li>{t('regie.setup.cable1')}</li>
                    <li>{auto ? t('regie.setup.cable2auto') : t('regie.setup.cable2manual')}</li>
                    <li>{t('regie.setup.cable3')}</li>
                </ol>
                <button type="button" className={styles.primary} onClick={sendToProjector}>
                    {screens === 'prompt' ? t('regie.setup.allow') : t('regie.setup.send')}
                </button>
                {extended !== false && (
                    <button type="button" className={styles.link} onClick={() => setStep('mirror')}>
                        {t('regie.setup.mirrorLink')}
                    </button>
                )}
            </>
        );
    } else if (step === 'moveFirst') {
        body = (
            <>
                <h2 className={styles.title}>{t('regie.setup.moveFirstTitle')}</h2>
                <p className={styles.p}>{t('regie.setup.moveFirstText')}</p>
                <button type="button" className={styles.primary} onClick={sendToProjector}>
                    {t('regie.setup.moveFirstDone')}
                </button>
            </>
        );
    } else if (step === 'phone') {
        body = (
            <>
                <h2 className={styles.title}>{t('regie.setup.phoneTitle')}</h2>
                <div className={styles.phone}>
                    <span className={styles.qr}>
                        <QRCodeSVG value={regieLink} size={180} />
                    </span>
                    <ol className={styles.steps}>
                        <li>{t('regie.setup.phone1')}</li>
                        <li>{t('regie.setup.phone2')}</li>
                    </ol>
                </div>
                <button type="button" className={styles.primary} onClick={thisIsPublic}>
                    {t('regie.setup.phoneGo')}
                </button>
            </>
        );
    } else {
        body = (
            <>
                <h2 className={styles.title}>{t('regie.setup.blockedTitle')}</h2>
                <p className={styles.p}>{t('regie.setup.blockedText')}</p>
                <a
                    className={styles.primary}
                    href={publicScreenURL(client.id, gameID)}
                    target="unboared-ecran-public"
                    onClick={() => navigate(regieURL(client.id))}
                >
                    {t('regie.setup.blockedOpen')}
                </a>
            </>
        );
    }

    return (
        <>
            <button type="button" className={styles.trigger} onClick={start}>
                <IconScreens />
                <span>
                    {t('regie.button')}
                    {(extended || again) && knownAccess && (
                        <small>{again ? t('regie.buttonAgain') : t('regie.buttonDetected')}</small>
                    )}
                </span>
            </button>
            {open &&
                createPortal(
                    <div
                        className={styles.scrim}
                        role="presentation"
                        onClick={(e) => e.target === e.currentTarget && close()}
                    >
                        <div className={styles.dialog} role="dialog" aria-modal="true">
                            <button
                                type="button"
                                className={styles.x}
                                onClick={close}
                                aria-label={t('regie.setup.close')}
                            >
                                <LuX />
                            </button>
                            {body}
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
}
